import { createGlobalState } from '@vueuse/core';
import type { VxBadgeColor } from '@voxie/frontend-components';
import { computed, ref } from 'vue';
import type { ThreadCountItem } from '~/services/threads/threadsClient.types';
import type { ThreadCategoryType } from '~/services/threadCategories/threadCategoriesClient.types';
import { THREAD_SECTIONS, type ThreadSectionValue } from '~/constants/messageHub';
import { aiClient, threadCategoriesClient, threadsClient } from '~/services';
import { useCurrentGroup } from '~/composables/useCurrentGroup';
import { useToasted } from '~/composables/useToasted';
import debounce from '~/utils/debounce';
import { catchRequestError } from '~/utils/request';

export type IntelligentSection = {
    sectionValue: ThreadSectionValue;
    label: string;
    badgeColor: VxBadgeColor;
    categories: ThreadCategoryType[];
};

export const uncategorized: ThreadCategoryType = {
    name: 'Uncategorized',
    color: '94a3b8',
    description: null,
    classification_instructions: '',
};

export const useIntelligentInbox = createGlobalState(() => {
    const toasted = useToasted();

    const teamId = Spark.state.currentTeam.id;
    const { getContext } = useCurrentGroup();
    const activeSection = ref<ThreadSectionValue | null>(null);
    const categories = ref<ThreadCategoryType[] | undefined>(undefined);
    const loadingCategories = ref(false);

    const fetchCategories = async (): Promise<void> => {
        if (loadingCategories.value || categories.value !== undefined) {
            return;
        }
        loadingCategories.value = true;
        try {
            const response = await threadCategoriesClient.getThreadCategories({ teamId });
            categories.value = response.data.categories;
        } catch (err) {
            toasted.error(catchRequestError(err));
            categories.value = [];
        } finally {
            loadingCategories.value = false;
        }
    };

    const refreshCategories = async (): Promise<void> => {
        if (loadingCategories.value) {
            return;
        }
        loadingCategories.value = true;
        try {
            const response = await threadCategoriesClient.getThreadCategories({ teamId });
            categories.value = response.data.categories;
        } catch {
            // Silent fail on background refresh
        } finally {
            loadingCategories.value = false;
        }
    };

    const sections = computed<IntelligentSection[]>(() => {
        return THREAD_SECTIONS.map((def) => ({
            sectionValue: def.value,
            label: def.label,
            badgeColor: def.badgeColor,
            categories: [...(categories.value ?? []), uncategorized],
        }));
    });

    const counts = ref<ThreadCountItem[] | undefined>(undefined);
    const loadingCounts = ref(false);

    const fetchCounts = async (): Promise<void> => {
        if (loadingCounts.value || counts.value !== undefined) {
            return;
        }
        loadingCounts.value = true;
        try {
            const response = await threadsClient.getCounts(getContext());
            counts.value = response.data.data;
        } catch (error) {
            toasted.error(catchRequestError(error));
            counts.value = [];
        } finally {
            loadingCounts.value = false;
        }
    };

    const refreshCounts = debounce(async (): Promise<void> => {
        try {
            const response = await threadsClient.getCounts(getContext());
            counts.value = response.data.data;
        } catch {
            // Silent fail on background refresh -- next event will retry
        }
    }, 2000);

    const sectionCounts = computed<Record<string, number>>(() => {
        const result: Record<string, number> = {};
        for (const item of counts.value ?? []) {
            result[item.thread_section] = (result[item.thread_section] ?? 0) + item.count;
        }
        return result;
    });

    const categoryCounts = computed<Record<string, Record<string, number>>>(() => {
        const result: Record<string, Record<string, number>> = {};
        for (const item of counts.value ?? []) {
            const section = item.thread_section;
            if (!result[section]) {
                result[section] = {};
            }
            const categoryKey = String(item.thread_category_id ?? 'uncategorized');
            result[section][categoryKey] = item.count;
        }
        return result;
    });

    const suggestionsBucket = new Map<string, string>();
    const suggestionRequests = new Map<string, Promise<string>>();

    const suggestionKey = (groupId: string, contactId: number): string => {
        return `${groupId}:${contactId}`;
    };

    const fetchSuggestion = (groupId: string, contactId: number): Promise<string> => {
        const key = suggestionKey(groupId, contactId);

        if (suggestionsBucket.has(key)) {
            return Promise.resolve(suggestionsBucket.get(key) ?? '');
        }

        const existingRequest = suggestionRequests.get(key);
        if (existingRequest) {
            return existingRequest;
        }

        const request = aiClient
            .messageHubSuggestions({ teamId, groupId }, { contact_id: contactId })
            .then((response) => {
                const result = response.data.data;
                const reply = result?.message ?? '';
                suggestionsBucket.set(key, reply);
                return reply;
            })
            .catch((error) => {
                console.error(error);
                return '';
            })
            .finally(() => {
                suggestionRequests.delete(key);
            });

        suggestionRequests.set(key, request);

        return request;
    };

    const invalidateSuggestion = (groupId: string, contactId: number): void => {
        const key = suggestionKey(groupId, contactId);
        suggestionsBucket.delete(key);
        suggestionRequests.delete(key);
    };

    return {
        activeSection,
        categories,
        loadingCategories,
        fetchCategories,
        refreshCategories,
        counts,
        loadingCounts,
        fetchCounts,
        refreshCounts,
        sectionCounts,
        categoryCounts,
        sections,
        fetchSuggestion,
        invalidateSuggestion,
    };
});
